<template>
  <div class="min-h-screen bg-gray-100 pb-20">
    <div class="max-w-[600px] mx-auto">
      
      <!-- Header -->
      <div class="bg-white sticky top-0 z-10 shadow-sm">
        <div class="flex items-center gap-3 p-4">
          <button @click="$router.back()" class="p-1">
            <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <h1 class="text-lg font-bold text-gray-800 flex-1">Takaslarım</h1>
        </div>
      </div>

      <!-- İstatistik Özeti -->
      <div class="bg-gradient-to-r from-takas-sari to-yellow-400 p-4">
        <div class="grid grid-cols-4 gap-2 text-center">
          <div>
            <div class="text-2xl font-bold text-takas-koyu">{{ stats.total }}</div>
            <div class="text-xs text-takas-koyu/80">Toplam</div>
          </div>
          <div>
            <div class="text-2xl font-bold text-orange-600">{{ stats.pending }}</div>
            <div class="text-xs text-takas-koyu/80">Bekleyen</div>
          </div>
          <div>
            <div class="text-2xl font-bold text-green-600">{{ stats.completed }}</div>
            <div class="text-xs text-takas-koyu/80">Başarılı</div>
          </div>
          <div>
            <div class="text-2xl font-bold text-blue-600">{{ stats.avgResponseTime }}</div>
            <div class="text-xs text-takas-koyu/80">Ort. Cevap</div>
          </div>
        </div>
      </div>

      <!-- Sekmeler -->
      <div class="bg-white border-b border-gray-200">
        <div class="flex">
          <button 
            @click="activeTab = 'incoming'"
            :class="[
              'flex-1 py-3 text-sm font-medium transition relative',
              activeTab === 'incoming' ? 'text-takas-sari' : 'text-gray-500'
            ]"
          >
            Gelen Teklifler
            <span v-if="incomingOffers.filter(o => o.status === 'pending').length" 
                  class="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {{ incomingOffers.filter(o => o.status === 'pending').length }}
            </span>
            <div v-if="activeTab === 'incoming'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-takas-sari"></div>
          </button>
          <button 
            @click="activeTab = 'outgoing'"
            :class="[
              'flex-1 py-3 text-sm font-medium transition relative',
              activeTab === 'outgoing' ? 'text-takas-sari' : 'text-gray-500'
            ]"
          >
            Giden Teklifler
            <div v-if="activeTab === 'outgoing'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-takas-sari"></div>
          </button>
          <button 
            @click="activeTab = 'completed'"
            :class="[
              'flex-1 py-3 text-sm font-medium transition relative',
              activeTab === 'completed' ? 'text-takas-sari' : 'text-gray-500'
            ]"
          >
            Tamamlananlar
            <div v-if="activeTab === 'completed'" class="absolute bottom-0 left-0 right-0 h-0.5 bg-takas-sari"></div>
          </button>
        </div>
      </div>

      <!-- Filtre ve Sıralama -->
      <div class="bg-white p-3 border-b border-gray-100">
        <div class="flex gap-2">
          <div class="flex-1 relative">
            <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="İlan adına göre ara..."
              class="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-lg text-sm focus:outline-none focus:border-takas-sari"
            />
          </div>
          <select v-model="sortBy" class="px-3 py-2 bg-gray-100 rounded-lg text-sm">
            <option value="newest">En yeni</option>
            <option value="oldest">En eski</option>
          </select>
          <select v-model="statusFilter" class="px-3 py-2 bg-gray-100 rounded-lg text-sm" v-if="activeTab !== 'completed'">
            <option value="all">Tümü</option>
            <option value="pending">Bekleyen</option>
            <option value="accepted">Kabul edilen</option>
            <option value="rejected">Reddedilen</option>
          </select>
        </div>
      </div>

      <!-- İçerik -->
      <div class="p-4 space-y-3">
        
        <!-- Gelen Teklifler -->
        <div v-if="activeTab === 'incoming'">
          <div v-if="filteredIncomingOffers.length === 0" class="text-center py-12">
            <div class="text-gray-400 mb-3">
              <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
              </svg>
            </div>
            <p class="text-gray-500">Henüz gelen teklif yok</p>
          </div>
          <div v-for="offer in filteredIncomingOffers" :key="offer.id" class="bg-white rounded-xl p-3 shadow-sm">
            <div class="flex gap-3">
              <div class="w-16 h-16 bg-gradient-to-br from-takas-sari/30 to-yellow-400/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-2xl">{{ offer.myListing.icon }}</span>
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div>
                    <h3 class="font-semibold text-gray-800">{{ offer.myListing.title }}</h3>
                    <p class="text-xs text-gray-500 mt-0.5">{{ offer.myListing.category }}</p>
                  </div>
                  <span :class="getStatusClass(offer.status)" class="text-xs px-2 py-0.5 rounded-full">
                    {{ getStatusText(offer.status) }}
                  </span>
                </div>
                
                <!-- Teklif Eden Bilgisi -->
                <div class="flex items-center gap-2 mt-2">
                  <div class="w-5 h-5 rounded-full bg-takas-sari/50 flex items-center justify-center">
                    <span class="text-[10px] font-bold">{{ offer.fromUser.initial }}</span>
                  </div>
                  <span class="text-xs text-gray-600">{{ offer.fromUser.name }}</span>
                  <div class="flex items-center gap-0.5">
                    <svg class="w-3 h-3 text-takas-sari" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                    <span class="text-xs">{{ offer.fromUser.rating }}</span>
                  </div>
                </div>

                <!-- Teklif Edilen İlan -->
                <div class="mt-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                  <span class="text-sm">🔄</span>
                  <span class="text-xs text-gray-600">Teklif ettiği:</span>
                  <span class="text-xs font-medium">{{ offer.offerListing.title }}</span>
                  <span v-if="offer.cashDifference" class="text-xs text-green-600 ml-auto">{{ offer.cashDifference }} TL</span>
                </div>

                <p class="text-xs text-gray-400 mt-2">{{ offer.createdAt }}</p>

                <!-- Aksiyon Butonları -->
                <div class="flex gap-2 mt-3" v-if="offer.status === 'pending'">
                  <button @click="acceptOffer(offer)" class="flex-1 bg-green-500 text-white py-1.5 rounded-lg text-sm font-medium">
                    Kabul Et
                  </button>
                  <button @click="rejectOffer(offer)" class="flex-1 bg-red-500 text-white py-1.5 rounded-lg text-sm font-medium">
                    Reddet
                  </button>
                  <button @click="goToChat(offer)" class="px-3 py-1.5 border border-gray-300 rounded-lg">
                    <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4-.8L3 20l1.8-3.6A7.94 7.94 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Giden Teklifler -->
        <div v-if="activeTab === 'outgoing'">
          <div v-if="filteredOutgoingOffers.length === 0" class="text-center py-12">
            <p class="text-gray-500">Henüz giden teklif yok</p>
          </div>
          <div v-for="offer in filteredOutgoingOffers" :key="offer.id" class="bg-white rounded-xl p-3 shadow-sm">
            <div class="flex gap-3">
              <div class="w-16 h-16 bg-gradient-to-br from-takas-sari/30 to-yellow-400/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-2xl">{{ offer.offerListing.icon }}</span>
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div>
                    <h3 class="font-semibold text-gray-800">{{ offer.offerListing.title }}</h3>
                    <p class="text-xs text-gray-500 mt-0.5">{{ offer.offerListing.category }}</p>
                  </div>
                  <span :class="getStatusClass(offer.status)" class="text-xs px-2 py-0.5 rounded-full">
                    {{ getStatusText(offer.status) }}
                  </span>
                </div>

                <!-- Kime Teklif Yapıldı -->
                <div class="flex items-center gap-2 mt-2">
                  <div class="w-5 h-5 rounded-full bg-takas-sari/50 flex items-center justify-center">
                    <span class="text-[10px] font-bold">{{ offer.toUser.initial }}</span>
                  </div>
                  <span class="text-xs text-gray-600">{{ offer.toUser.name }}</span>
                </div>

                <!-- Teklif Ettiğim İlan -->
                <div class="mt-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                  <span class="text-sm">📦</span>
                  <span class="text-xs text-gray-600">Teklif ettiğim:</span>
                  <span class="text-xs font-medium">{{ offer.myListing.title }}</span>
                  <span v-if="offer.cashDifference" class="text-xs text-green-600 ml-auto">{{ offer.cashDifference }} TL</span>
                </div>

                <p class="text-xs text-gray-400 mt-2">{{ offer.createdAt }}</p>

                <!-- Aksiyon Butonları -->
                <div class="flex gap-2 mt-3" v-if="offer.status === 'pending'">
                  <button @click="cancelOffer(offer)" class="flex-1 bg-gray-500 text-white py-1.5 rounded-lg text-sm font-medium">
                    İptal Et
                  </button>
                  <button @click="goToChat(offer)" class="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg">
                    Mesaj Gönder
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tamamlanan Takaslar -->
        <div v-if="activeTab === 'completed'">
          <div v-if="completedTrades.length === 0" class="text-center py-12">
            <p class="text-gray-500">Henüz tamamlanan takas yok</p>
          </div>
          <div v-for="trade in completedTrades" :key="trade.id" class="bg-white rounded-xl p-3 shadow-sm">
            <div class="flex gap-3">
              <div class="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="text-2xl">✅</span>
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div>
                    <h3 class="font-semibold text-gray-800">{{ trade.title }}</h3>
                    <p class="text-xs text-gray-500 mt-0.5">{{ trade.category }}</p>
                  </div>
                  <span class="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Tamamlandı</span>
                </div>

                <div class="flex items-center gap-2 mt-2">
                  <div class="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <span class="text-[10px] font-bold text-white">{{ trade.withUser.initial }}</span>
                  </div>
                  <span class="text-xs text-gray-600">{{ trade.withUser.name }} ile takas yapıldı</span>
                </div>

                <div class="flex items-center gap-3 mt-2 text-xs text-gray-400">
                  <span>{{ trade.completedDate }}</span>
                  <span>•</span>
                  <span class="text-green-600">{{ trade.tradeValue }}</span>
                </div>

                <button @click="rateUser(trade)" class="mt-2 text-takas-sari text-xs font-medium">
                  Kullanıcıyı Değerlendir →
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MyTrades',
  data() {
    return {
      activeTab: 'incoming',
      searchQuery: '',
      sortBy: 'newest',
      statusFilter: 'all',
      stats: {
        total: 24,
        pending: 5,
        completed: 16,
        avgResponseTime: '2s'
      },
      incomingOffers: [
        {
          id: 1,
          status: 'pending',
          createdAt: '2 saat önce',
          cashDifference: 500,
          myListing: {
            id: 1,
            title: 'iPhone 14 Pro',
            category: 'Telefon',
            icon: '📱'
          },
          offerListing: {
            id: 101,
            title: 'MacBook Air M1',
            category: 'Bilgisayar',
            icon: '💻'
          },
          fromUser: {
            name: 'Mehmet Demir',
            initial: 'M',
            rating: 4.8
          }
        },
        {
          id: 2,
          status: 'accepted',
          createdAt: '1 gün önce',
          cashDifference: null,
          myListing: {
            id: 2,
            title: 'PlayStation 5',
            category: 'Oyun',
            icon: '🎮'
          },
          offerListing: {
            id: 102,
            title: 'Samsung TV',
            category: 'Elektronik',
            icon: '📺'
          },
          fromUser: {
            name: 'Ayşe Yılmaz',
            initial: 'A',
            rating: 4.9
          }
        }
      ],
      outgoingOffers: [
        {
          id: 3,
          status: 'pending',
          createdAt: '5 saat önce',
          cashDifference: 1000,
          myListing: {
            id: 1,
            title: 'iPhone 14 Pro',
            category: 'Telefon',
            icon: '📱'
          },
          offerListing: {
            id: 103,
            title: 'Samsung S23 Ultra',
            category: 'Telefon',
            icon: '📱'
          },
          toUser: {
            name: 'Can Öztürk',
            initial: 'C',
            rating: 4.7
          }
        }
      ],
      completedTrades: [
        {
          id: 4,
          title: 'iPhone 13 Pro',
          category: 'Telefon',
          tradeValue: 'Takas + 2000₺',
          completedDate: '15 Aralık 2023',
          withUser: {
            name: 'Zeynep Kaya',
            initial: 'Z'
          }
        }
      ]
    }
  },
  computed: {
    filteredIncomingOffers() {
      let result = [...this.incomingOffers]
      if (this.statusFilter !== 'all') {
        result = result.filter(o => o.status === this.statusFilter)
      }
      if (this.searchQuery) {
        result = result.filter(o => 
          o.myListing.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }
      if (this.sortBy === 'newest') {
        result.reverse()
      }
      return result
    },
    filteredOutgoingOffers() {
      let result = [...this.outgoingOffers]
      if (this.statusFilter !== 'all') {
        result = result.filter(o => o.status === this.statusFilter)
      }
      if (this.searchQuery) {
        result = result.filter(o => 
          o.offerListing.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }
      if (this.sortBy === 'newest') {
        result.reverse()
      }
      return result
    }
  },
  methods: {
    getStatusClass(status) {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-600',
        accepted: 'bg-green-100 text-green-600',
        rejected: 'bg-red-100 text-red-600',
        cancelled: 'bg-gray-100 text-gray-600'
      }
      return classes[status] || classes.pending
    },
    getStatusText(status) {
      const texts = {
        pending: 'Bekliyor',
        accepted: 'Kabul Edildi',
        rejected: 'Reddedildi',
        cancelled: 'İptal Edildi'
      }
      return texts[status] || status
    },
    acceptOffer(offer) {
      console.log('Teklif kabul edildi:', offer)
      alert('Teklif kabul edildi!')
    },
    rejectOffer(offer) {
      console.log('Teklif reddedildi:', offer)
      alert('Teklif reddedildi')
    },
    cancelOffer(offer) {
      console.log('Teklif iptal edildi:', offer)
      alert('Teklifiniz iptal edildi')
    },
    goToChat(offer) {
      this.$router.push(`/chat/${offer.id}`)
    },
    rateUser(trade) {
      console.log('Kullanıcı değerlendiriliyor:', trade)
      alert('Değerlendirme sayfası açılacak')
    }
  }
}
</script>